import { ClipboardModule } from '@angular/cdk/clipboard'
import { CommonModule } from '@angular/common'
import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule } from '@angular/forms'

import { Store } from '@ngrx/store'
import { keyBy as _keyBy, some as _some } from 'lodash/fp'
import { ImageModule } from 'primeng/image'
import { SelectModule } from 'primeng/select'
import { TooltipModule } from 'primeng/tooltip'

import { catchError, forkJoin, map, of } from 'rxjs'

import { FlxFormControlModule } from '@flowx/angular-ui-toolkit'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import {
  AttachedFile,
  AttachedFileType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import { MessageType } from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import {
  buildMessageOutcomeId,
  buildMessageSummaryId,
  getAvailableUploadOptions,
} from '@flowx/designer/modules/ai/conversation/conversation.utils'
import { AiChatMsgActionCardComponent } from '@flowx/designer/modules/ai/modules/chat/components/ai-chat-msg-action-card/ai-chat-msg-action-card.component'
import {
  AiAnalystContext,
  ChatMessage,
  ContextDocument,
  FeedbackType,
} from '@flowx/designer/modules/ai/types/ai.types'
import { DocumentPluginApi } from '@flowx/designer/shared/api/document-plugin.api'
import { AppStores } from '@flowx/designer/shared/effector/app/app.stores'

import { AnalystSummaryComponent } from '../../../../agents/analyst/components/ai-analyst-summary/ai-analyst-summary.component'
import { AiMsgSourcesComponent } from '../../../../agents/assistant/components/ai-msg-sources/ai-msg-sources.component'
import { DeveloperDependencySummaryComponent } from '../../../../agents/developer/components/developer-dependency-summary/developer-dependency-summary.component'
import { AiMsgFileActionsComponent } from '../../../../agents/documents/components/ai-msg-file-actions/ai-msg-file-actions.component'
import { AiMsgFileGeneratorsComponent } from '../../../../agents/documents/components/ai-msg-file-generators/ai-msg-file-generators.component'
import { AiMsgFilePreviewComponent } from '../../../../agents/documents/components/ai-msg-file-preview/ai-msg-file-preview.component'
import { AiIconsModule } from '../../../../common/icons/ai-icons.module'
import { FlxAiMarkdownPipe } from '../../../../common/pipes/flx-ai-markdown.pipe'
import { AiDmKeyRow } from '../../../../components/data-model/ai-dm-key-row.model'
import { AiDmSummaryService } from '../../../../components/data-model/ai-dm-summary.service'
import { AiDmSummaryComponent } from '../../../../components/data-model/ai-dm-summary/ai-dm-summary.component'
import { ExecutionPlanEditorComponent } from '../../../../components/execution-plan-editor/execution-plan-editor.component'
import { AiChatBubbleActions } from '../../../../conversation/conversation.actions'
import { AiSelectors } from '../../../../store/ai.selectors'
import { AiDestinationSelectorComponent } from '../ai-destination-selector/ai-destination-selector.component'
import { AiFeedbackActionsComponent } from '../ai-feedback-actions/ai-feedback-actions.component'

@Component({
  selector: 'flx-ai-chat-bubble',
  templateUrl: './ai-chat-bubble.component.html',
  styleUrls: ['./ai-chat-bubble.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ClipboardModule,
    ImageModule,
    TooltipModule,
    FlxAiMarkdownPipe,
    AiIconsModule,
    AnalystSummaryComponent,
    DeveloperDependencySummaryComponent,
    AiMsgSourcesComponent,
    AiFeedbackActionsComponent,
    AiMsgFilePreviewComponent,
    AiDmSummaryComponent,
    AiMsgFileGeneratorsComponent,
    AiMsgFileActionsComponent,
    SelectModule,
    FlxFormControlModule,
    AiChatMsgActionCardComponent,
    AiDestinationSelectorComponent,
    ExecutionPlanEditorComponent,
  ],
})
export class AiChatBubbleComponent {
  #store = inject(Store)
  #destroyRef = inject(DestroyRef)
  #documentPluginApi = inject(DocumentPluginApi)
  #dmSummary = inject(AiDmSummaryService)

  #filesLoaded = false

  message = input.required<ChatMessage>()
  loading = input<boolean>(false)

  hasMessageActions = computed(
    () => this.message().actions && _some(Object.values(this.message().actions || {}))
  )

  latestMessageFromAgent = this.#store.selectSignal(ConversationSelectors.latestMessageFromAgent)
  lastMessageFromActiveNonDefaultThread = this.#store.selectSignal(
    ConversationSelectors.lastMessageFromActiveNonDefaultThread
  )
  isMessageLast = computed(() => {
    const lastMessage = this.lastMessageFromActiveNonDefaultThread()
    if (!lastMessage) {
      return false
    }

    return [
      lastMessage.id,
      buildMessageOutcomeId(lastMessage.id),
      buildMessageSummaryId(lastMessage.id),
    ].includes(this.message().id)
  })

  sendFeedback = output<FeedbackType>()

  isMessageFirst = computed(() => {
    const firstMessage = this.#store.selectSignal(
      ConversationSelectors.firstMessageFromActiveAgent
    )()
    const message = this.message()

    return !!firstMessage && firstMessage.id === message.id
  })

  uiHintedAction = this.#store.selectSignal(ConversationSelectors.uiHintedAction)

  msgFileActions = computed(() => {
    const hintedAction = this.uiHintedAction()

    // Only show file actions if there's a UI-hinted action
    if (!hintedAction) {
      return null
    }

    return {
      ...getAvailableUploadOptions(hintedAction.agent),
      checkDocumentContext: ![AgentAction.Expression, AgentAction.ComputedValue].includes(
        hintedAction.action
      ),
    }
  })

  shouldShowFileActions = computed(
    () => this.isMessageFirst() && this.isMessageLast() && this.msgFileActions() !== null
  )

  documents = computed(() => this.message().documents?.files || [])
  showDocuments = computed(() => !!this.message().documents)

  // Files attached via hint action context - displayed only if it's not a default thread message
  attachedFileReferences = computed<AttachedFile[]>(() => {
    const defaultThreadMessagesById = _keyBy(
      'id',
      this.#store.selectSignal(ConversationSelectors.defaultThread)()?.messages || []
    )
    if (!defaultThreadMessagesById[this.message()?.id]) {
      return this.message().attachedFiles || []
    }
    return []
  })

  // Signal to hold loaded files as File objects for the preview component
  loadedAttachedFiles = signal<File[]>([])

  // Effect to load files when attachedFileReferences changes (only once)
  loadAttachedFilesEffect = effect(() => {
    const fileRefs = this.attachedFileReferences()

    if (this.#filesLoaded || !fileRefs || fileRefs.length === 0) {
      return
    }

    this.#filesLoaded = true

    const loadRequests = fileRefs.map((ref) =>
      this.#documentPluginApi.downloadFile(ref.id).pipe(
        map(({ blob }) => {
          const { filename, mimeType } = this.#getFileMetadata(ref.type)
          return new File([blob], filename, { type: mimeType })
        }),
        catchError(() => of(null))
      )
    )

    forkJoin(loadRequests)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((files) => {
        this.loadedAttachedFiles.set(files.filter((f) => f !== null))
      })
  })

  isDocumentDisabled = computed(
    () =>
      this.#store.selectSignal(AiSelectors.hasSelectedDocument)() ||
      this.#store.selectSignal(AiSelectors.pendingAiResponse)()
  )

  isUserMessage = computed(() => this.message().type === MessageType.UserMsg)
  isErrorMessage = computed(() => this.message().type === MessageType.ErrorMsg)
  userInitials = AppStores.userProfile$.getState()?.initials

  canShowCopyToChatButton = computed(() => {
    const agent = this.message().agent

    return agent !== AgentName.ConnectedGraph && !this.message().executionPlanAction
  })

  isCopyMessageAllowed = computed(
    () => !this.loading() && this.message().message && this.message().message?.trim() !== ''
  )
  copiedTextToChat = signal(false)
  copyTextToChatTooltip = computed(() =>
    this.isCopyMessageAllowed() ? (this.copiedTextToChat() ? 'Copied!' : 'Copy to chat') : null
  )
  copyTextToChatIcon = computed(() => (!this.copiedTextToChat() ? 'copy-prompt' : 'circle-check'))

  changes = computed(() => {
    const message = this.message()
    const context = message.context as AiAnalystContext

    return { added: context?.changes?.added, removed: context?.changes?.removed }
  })

  dependencyChanges = computed(() => this.message().dependencyChanges ?? null)
  showFeedback = computed(() => {
    const { type } = this.message()
    const isLoading = this.loading()

    return !isLoading && type === MessageType.AiMsg
  })

  dmSummary = computed(() => this.#dmSummary.buildSummary(this.message()))

  isEditable = computed(() => this.#dmSummary.isEditable(this.message()))

  destinationSelector = computed(() => {
    const message = this.message()

    return message.destinationSelector
  })

  copyToChatInputField(): void {
    this.#store.dispatch(AiChatBubbleActions.copyToChatInputField({ text: this.message().message }))
    this.copiedTextToChat.set(true)
    setTimeout(() => this.copiedTextToChat.set(false), 2000)
  }

  submitFeedback(feedbackType: FeedbackType): void {
    this.sendFeedback.emit(feedbackType)
  }

  onContextDocumentClick(document: ContextDocument): void {
    this.#store.dispatch(AiChatBubbleActions.documentClick({ document }))
  }

  onAssignPagesToUserTasksClick(): void {
    this.#store.dispatch(AiChatBubbleActions.assignPagesToUserTasks())
  }

  onDmRowsUpdate(rows: AiDmKeyRow[]): void {
    this.#dmSummary.applyRowsUpdate(this.message(), rows)
  }

  #getFileMetadata(type: AttachedFileType): { filename: string; mimeType: string } {
    switch (type) {
      case 'image':
        return { filename: 'image.png', mimeType: 'image/png' }
      case 'bpmn':
        return { filename: 'process.bpmn', mimeType: 'application/xml' }
      case 'document':
        return { filename: 'attachment', mimeType: 'application/octet-stream' }
    }
  }
}
